import { Component, inject, input, model, OnInit, output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { AdvanceInvoiceStateEnums } from 'src/app/enum/advance-invoice-state-enum';
import { AdvanceInvoiceConceptModel } from 'src/app/models/invoicing/advance-invoice-concept.model';
import { AdvanceInvoiceModel } from 'src/app/models/invoicing/advance-invoice.model';
import { AdvanceInvoiceConceptService } from 'src/app/services/invoicing/advance-invoice-concept.service';
import { InvoiceDetailService } from 'src/app/services/invoicing/invoice-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-advance-invoice-concept',
  templateUrl: './register-advance-invoice-concept.component.html',
  styleUrls: ['./register-advance-invoice-concept.component.scss'],
  standalone: false
})
export class RegisterAdvanceInvoiceConceptComponent implements OnInit {

  private readonly advanceInvoiceConceptService = inject(AdvanceInvoiceConceptService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly invoiceDetailService = inject(InvoiceDetailService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  advanceInvoiceConceptContract = model<AdvanceInvoiceConceptModel>(new AdvanceInvoiceConceptModel());
  advanceInvoiceModel = input<AdvanceInvoiceModel>(new AdvanceInvoiceModel());
  isFormView = output<boolean>();
  updateListConcept = output<boolean>();

  advanceInvoiceConceptId: number = 0;
  disableButtonSave: boolean = false;
  formAdvanceInvoiceConcept: FormGroup = new FormGroup({});
  listConceptInvoicing: any[] = [];

  async ngOnInit() {
    this.validateForm(this.advanceInvoiceConceptContract());
    await this.getAllLists();
  }

  ngOnChanges(changes: SimpleChanges) {
    const advanceInvoiceConceptContract = changes["advanceInvoiceConceptContract"];
    if (advanceInvoiceConceptContract) {
      if (this.advanceInvoiceConceptContract().advanceInvoiceConceptId !== 0) {
        this.advanceInvoiceConceptId = this.advanceInvoiceConceptContract().advanceInvoiceConceptId;
        this.validateForm(this.advanceInvoiceConceptContract());
      }
    }
  }

  cancel() {
    this.clearForm();
    this.isFormView.emit(false);
  }

  clearForm() {
    this.advanceInvoiceConceptId = 0;
    this.advanceInvoiceConceptContract.set(new AdvanceInvoiceConceptModel());
    this.validateForm(this.advanceInvoiceConceptContract());
    this.isFormView.emit(false);
  }

  async getAllLists() {
    try {
      const [conceptsInvoicing] = await Promise.all([
        firstValueFrom(this.invoiceDetailService.getAllConceptInvoicingForSelectAsync()),
      ]);
      this.listConceptInvoicing = conceptsInvoicing;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas.', error);
      return false;
    }
  }

  async send() {
    Object.keys(this.formAdvanceInvoiceConcept.controls).forEach((key) => {
      const control = this.formAdvanceInvoiceConcept.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formAdvanceInvoiceConcept.valid) {
      const modelAdvanceInvoiceConcept = this.formAdvanceInvoiceConcept.getRawValue() as AdvanceInvoiceConceptModel;
      if (this.advanceInvoiceConceptId !== 0) {
        this.advanceInvoiceConceptContract().differenceValueFinal = modelAdvanceInvoiceConcept.differenceValueFinal;
        try {
          await firstValueFrom(this.advanceInvoiceConceptService.updateAdvanceInvoiceConcept(this.advanceInvoiceConceptContract()));
          this.toastHandlerService.showToast('success', 'El concepto de la factura anticipada fue modificado exitosamente.');
          this.clearForm()
          this.updateListConcept.emit(true);
        } catch (error) {
          this.updateListConcept.emit(true);
          console.error('Error al actualizar el concepto de la factura anticipada.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: any = {
      invoiceId: {
        required: 'El campo factura es obligatorio.',
        min: 'Debe seleccionar una factura válida.'
      },
      conceptInvoicingId: {
        required: 'El campo concepto es obligatorio.',
        min: 'Debe seleccionar un concepto válido.'
      },
      differenceValue: {
        required: 'El campo diferencia es obligatorio.',
        min: 'El valor de la diferencia debe ser mayor o igual a 0.'
      },
      differenceValueFinal: {
        required: 'El campo diferencia final es obligatorio.',
        min: 'El valor de la diferencia final debe ser mayor o igual a 0.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formAdvanceInvoiceConcept.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm(advanceInvoiceConceptModel: AdvanceInvoiceConceptModel) {
    this.formAdvanceInvoiceConcept = this.formBuilder.group({
      invoiceId: [advanceInvoiceConceptModel.invoiceId, [Validators.required, Validators.min(1)]],
      conceptInvoicingId: [{ value: advanceInvoiceConceptModel.conceptInvoicingId, disabled: true }, [Validators.required, Validators.min(1)]],
      differenceValue: [{ value: advanceInvoiceConceptModel.differenceValue, disabled: true }, [Validators.required, Validators.min(0)]],
      differenceValueFinal: [advanceInvoiceConceptModel.differenceValueFinal, [Validators.required, Validators.min(0)]]
    });
    if (this.advanceInvoiceModel().advanceInvoiceState != AdvanceInvoiceStateEnums.open) this.disableButtonSave = true;
  }

}
